<script setup>
import { RouterView } from 'vue-router'
import Navbar from '@/components/Navbar.vue'
import Footbar from '@/components/Footbar.vue'
import Notification from '@/components/Notification.vue'
import ThemeSwitcher from '@/components/ThemeSwitcher.vue'
import { useTheme } from '@/composables/useTheme.js'
import { useChatStore } from '@/store/chat'
import { useAuthStore } from '@/store/auth'
import { ref, watch, onBeforeUnmount } from 'vue'

// Initialise theme system
const { isDark } = useTheme()

const authStore = useAuthStore()
const chatStore = useChatStore()

// Global chat polling — App.vue is always mounted so this never stops
// Watch isAuthenticated so we start as soon as auth resolves (from cookie or login)
watch(
  () => authStore.isAuthenticated,
  (authenticated) => {
    if (authenticated) {
      chatStore.stopChatsPolling() // clear any stale timer
      chatStore.fetchChats()        // immediate baseline fetch
      chatStore.startChatsPolling(5000)
    } else {
      chatStore.stopChatsPolling()
    }
  },
  { immediate: true }
)

onBeforeUnmount(() => chatStore.stopChatsPolling())

// Keep page title in sync with unread count
watch(
  () => chatStore.totalUnread,
  (count) => chatStore._updateTitle(count)
)

// Loading state
const isLoading = ref(false)
</script>

<template>
  <div :class="isDark ? 'dark' : ''">
    <Navbar />
    <Notification />
    <ThemeSwitcher />

    <!-- Conditional rendering of the Loading component -->
    <div v-if="isLoading" class="loading-wrapper">
      <div class="loading-container">
        <div class="loading-circle"></div>
        <div class="loading-circle"></div>
        <div class="loading-circle"></div>
      </div>
    </div>

    <RouterView :key="$route.params.id" class="pb-16 md:pb-0" />
    <Footbar />
  </div>
</template>

<style scoped>
/* Centering the loading component */
.loading-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
}

/* Loading container */
.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
}

/* Individual loading circles */
.loading-circle {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: var(--color-primary-400, #ff9e00);
  animation: bounce 1.2s infinite ease-in-out;
}

/* Adding different delays for a smooth effect */
.loading-circle:nth-child(1) {
  animation-delay: 0s;
}
.loading-circle:nth-child(2) {
  animation-delay: 0.2s;
}
.loading-circle:nth-child(3) {
  animation-delay: 0.4s;
}

/* 3D Bounce Animation */
@keyframes bounce {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-15px);
  }
}
</style>
